/**
 * /dashboard/messages — Member-side inbox + chat.
 *
 * Mirrors /coach/messages but slimmed to the member's POV: usually a single
 * conversation with their assigned coach. If `assigned_coach_id` is null,
 * we render a coach-picker on first visit; the first send "sticks" that
 * choice via the backend.
 *
 * Bubble UI (Messenger / iMessage style):
 *   - own messages right-aligned, brand-red
 *   - coach messages left-aligned, zinc
 *   - auto-scroll on new messages
 *   - 5-second poll while visible, paused when tab is backgrounded
 *
 * 2026-02-13 — Sachin spec: "keep it all in-site, like Messenger / IG chat".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, Send, MessageSquare, CheckCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import useMessageSocket from "@/hooks/useMessageSocket";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Two poll intervals:
//   FAST — when WS is offline (5s, the original behavior).
//   SLOW — safety net when WS is online (60s; live events drive the UI).
const POLL_MS = 5000;
const POLL_MS_WHEN_LIVE = 60000;

function fmtTime(iso) {
    if (!iso) return "";
    const t = new Date(iso);
    const now = new Date();
    const sameDay = t.toDateString() === now.toDateString();
    if (sameDay) return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    return t.toLocaleDateString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function MemberMessages() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [threads, setThreads] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [activeThread, setActiveThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [body, setBody] = useState("");
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const [pickerCoachId, setPickerCoachId] = useState("");
    const messagesEndRef = useRef(null);
    const composerRef = useRef(null);
    const lastMessageCountRef = useRef(0);

    // Realtime — open one socket for the page lifetime. Events are pushed
    // by the backend on send / read. When WS is offline, we fall back to
    // the existing 5-second poll automatically.
    const { connected: liveConnected, subscribe } = useMessageSocket();

    const initialTopic = useMemo(() => searchParams.get("topic") || "", [searchParams]);

    // Bounce coaches/owners to their inbox (this is the member-side page).
    useEffect(() => {
        if (authLoading) return;
        if (!user) { navigate("/", { replace: true }); return; }
        if (user.role === "owner" || user.role === "coach") {
            navigate("/coach/messages", { replace: true });
        }
        // 2026-07 (Sachin): kids can message too — bounce removed.
    }, [authLoading, user, navigate]);

    // Load threads + available coaches in parallel
    const loadAll = useCallback(async () => {
        try {
            const [tr, cr] = await Promise.all([
                axios.get(`${API}/messages/threads`, { withCredentials: true }),
                axios.get(`${API}/messages/available-coaches`, { withCredentials: true }),
            ]);
            const list = tr.data?.threads || [];
            setThreads(list);
            setCoaches(cr.data?.coaches || []);
            // Auto-open the first thread for the member (they typically only have one).
            if (list.length > 0) {
                setActiveThread((prev) => prev || list[0]);
            }
        } catch (e) {
            toast.error("Couldn't load messages", { description: e?.response?.data?.detail });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        Promise.resolve().then(() => loadAll());
    }, [loadAll]);

    // Prefill topic from query string (e.g. "Subscribe Jake") on first render.
    useEffect(() => {
        if (initialTopic && !body) {
            Promise.resolve().then(() => setBody(`Hi — about ${initialTopic}: `));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialTopic]);

    // Load messages of the active thread + poll while visible.
    const loadMessages = useCallback(async (threadId) => {
        if (!threadId) return;
        try {
            const r = await axios.get(`${API}/messages/threads/${threadId}`, { withCredentials: true });
            setMessages(r.data?.messages || []);
            // Update unread to 0 in the local thread list
            setThreads((prev) => prev.map((t) => t.thread_id === threadId ? { ...t, unread_for_client: 0 } : t));
        } catch (e) {
            // Silent on poll failures
            if (e?.response?.status !== 304) {
                // Only toast on first/manual load, not background polls
            }
        }
    }, []);

    useEffect(() => {
        if (!activeThread?.thread_id) return;
        Promise.resolve().then(() => loadMessages(activeThread.thread_id));
        let alive = true;
        const interval = liveConnected ? POLL_MS_WHEN_LIVE : POLL_MS;
        const tick = () => {
            if (document.visibilityState !== "visible") return;
            if (!alive) return;
            Promise.resolve().then(() => loadMessages(activeThread.thread_id));
        };
        const id = setInterval(tick, interval);
        const onVis = () => { if (document.visibilityState === "visible") tick(); };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            alive = false;
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, [activeThread?.thread_id, loadMessages, liveConnected]);

    // Push-based updates from the WS — append new messages live, flip the
    // "seen" tick when the coach reads our message.
    useEffect(() => {
        if (!activeThread?.thread_id) return undefined;
        return subscribe((evt) => {
            if (evt.type === "message:new" && evt.thread_id === activeThread.thread_id) {
                setMessages((prev) => {
                    if (prev.some((m) => m.message_id === evt.message.message_id)) return prev;
                    return [...prev, evt.message];
                });
                // If the inbound message is from the coach AND we're looking
                // at the thread, immediately mark-read on the server. (Calls
                // GET-thread, which sets unread_for_client = 0 + flips ticks.)
                if (evt.message.sender_user_id !== user?.user_id) {
                    Promise.resolve().then(() => loadMessages(activeThread.thread_id));
                }
            }
            if (evt.type === "thread:read" && evt.thread_id === activeThread.thread_id) {
                const at = new Date().toISOString();
                setMessages((prev) => prev.map((m) =>
                    m.sender_user_id === user?.user_id && !m.read_at_by_recipient
                        ? { ...m, read_at_by_recipient: at }
                        : m
                ));
            }
        });
    }, [activeThread?.thread_id, subscribe, user?.user_id, loadMessages]);

    // Auto-scroll to newest when message count grows
    useEffect(() => {
        if (messages.length > lastMessageCountRef.current) {
            messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
        }
        lastMessageCountRef.current = messages.length;
    }, [messages.length]);

    const send = async () => {
        const text = body.trim();
        if (!text) return;
        // 2026-07: coach-picker requirement removed — unassigned clients
        // route straight to the gym owner on the backend.
        setSending(true);
        try {
            const payload = { body: text };
            const r = await axios.post(`${API}/messages/send`, payload, { withCredentials: true });
            setBody("");
            // After send, ensure the thread is loaded
            const tid = r.data?.thread_id;
            if (tid) {
                // Refetch threads + open the new one
                const tr = await axios.get(`${API}/messages/threads`, { withCredentials: true });
                const list = tr.data?.threads || [];
                setThreads(list);
                const next = list.find((t) => t.thread_id === tid) || activeThread;
                if (next) setActiveThread(next);
                await loadMessages(tid);
            }
            composerRef.current?.focus();
        } catch (e) {
            toast.error("Couldn't send", { description: e?.response?.data?.detail });
        } finally {
            setSending(false);
        }
    };

    const onComposerKey = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            send();
        }
    };

    if (authLoading || loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }
    if (!user || user.role !== "client") return null;

    // 2026-07: first-time coach picker removed — everyone can message
    // immediately; unassigned clients auto-route to the owner.
    const needsPicker = false;

    return (
        <main data-testid="member-messages-page" className="min-h-screen bg-zinc-950 text-white">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-28">
                <div className="flex items-center justify-between mb-5">
                    <h1 className="font-display text-3xl sm:text-4xl tracking-wider">Messages</h1>
                    {threads.length > 1 && (
                        <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">
                            {threads.length} threads
                        </span>
                    )}
                </div>

                {/* Thread picker — only renders when there are 2+ threads */}
                {threads.length > 1 && (
                    <div className="flex gap-2 flex-wrap mb-5" data-testid="member-thread-picker">
                        {threads.map((t) => {
                            const active = activeThread?.thread_id === t.thread_id;
                            return (
                                <button
                                    type="button"
                                    key={t.thread_id}
                                    data-testid={`member-thread-tab-${t.thread_id}`}
                                    onClick={() => setActiveThread(t)}
                                    className={`text-[11px] tracking-[0.18em] uppercase px-3 py-2 border transition-colors ${
                                        active
                                            ? "border-brand-red bg-brand-red/10 text-white"
                                            : "border-zinc-800 text-zinc-300 hover:text-white"
                                    }`}
                                >
                                    {t.coach?.name || t.coach?.email || "Coach"}
                                    {(t.unread_for_client || 0) > 0 && !active && (
                                        <span className="ml-2 inline-flex items-center justify-center text-[10px] font-bold rounded-full bg-brand-red text-white px-1.5 min-w-[18px]">
                                            {t.unread_for_client}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}

                {needsPicker ? (
                    <CoachPicker
                        coaches={coaches}
                        pickedId={pickerCoachId}
                        onPick={setPickerCoachId}
                    />
                ) : null}

                {/* Active conversation pane */}
                {!needsPicker && activeThread && (
                    <ConversationHeader thread={activeThread} />
                )}

                <div
                    data-testid="member-message-list"
                    className="border border-zinc-900 bg-zinc-900/30 rounded-lg p-4 sm:p-5 min-h-[420px] max-h-[60vh] overflow-y-auto flex flex-col"
                >
                    {messages.length === 0 ? (
                        <EmptyState needsPicker={needsPicker} hasPicked={!!pickerCoachId} />
                    ) : (
                        <ul className="space-y-3 mt-auto">
                            {messages.map((m) => (
                                <MessageBubble key={m.message_id} m={m} myUserId={user.user_id} />
                            ))}
                            <div ref={messagesEndRef} />
                        </ul>
                    )}
                </div>

                {/* Composer */}
                <div className="mt-3 flex items-end gap-2">
                    <textarea
                        ref={composerRef}
                        data-testid="member-message-composer"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        onKeyDown={onComposerKey}
                        rows={2}
                        placeholder={
                            needsPicker && !pickerCoachId
                                ? "Pick a coach above, then type your message…"
                                : "Type a message…   (Enter to send · Shift+Enter for new line)"
                        }
                        disabled={needsPicker && !pickerCoachId}
                        className="flex-1 bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-brand-red resize-none disabled:opacity-50"
                    />
                    <button
                        type="button"
                        data-testid="member-message-send"
                        onClick={send}
                        disabled={!body.trim() || sending || (needsPicker && !pickerCoachId)}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 bg-brand-red text-white font-bold tracking-[0.15em] uppercase text-xs hover:bg-red-700 transition-colors disabled:opacity-40 rounded-lg"
                    >
                        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                        Send
                    </button>
                </div>

                <p className="text-[10px] text-zinc-500 mt-3 text-center tracking-wider">
                    Your coach is notified by email when you send a message. Replies arrive here.
                </p>
            </div>
        </main>
    );
}


function CoachPicker({ coaches, pickedId, onPick }) {
    if (!coaches.length) {
        return (
            <div
                data-testid="member-coach-picker-empty"
                className="border border-zinc-800 bg-zinc-900/40 rounded-lg p-5 mb-4 text-sm text-zinc-300"
            >
                No coaches are currently available to message. Please book an assessment first.
            </div>
        );
    }
    return (
        <div
            data-testid="member-coach-picker"
            className="border border-amber-700/40 bg-amber-900/10 rounded-lg p-4 sm:p-5 mb-4"
        >
            <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 mb-2">First message — pick a coach</div>
            <p className="text-xs text-zinc-300 mb-3">
                Your message will go to the coach you pick. After your first send, this becomes your default thread.
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
                {coaches.map((c) => (
                    <label
                        key={c.user_id}
                        data-testid={`member-coach-pick-${c.user_id}`}
                        className={`cursor-pointer flex items-center gap-3 p-2.5 border rounded-md transition-colors ${
                            pickedId === c.user_id
                                ? "border-brand-red bg-brand-red/10 text-white"
                                : "border-zinc-800 hover:border-zinc-600 text-zinc-300"
                        }`}
                    >
                        <input
                            type="radio"
                            name="coach-pick"
                            value={c.user_id}
                            checked={pickedId === c.user_id}
                            onChange={() => onPick(c.user_id)}
                            className="accent-brand-red"
                        />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold truncate">{c.name || c.email}</div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500">{c.role === "owner" ? "Owner" : "Coach"}</div>
                        </div>
                    </label>
                ))}
            </div>
        </div>
    );
}


function ConversationHeader({ thread }) {
    const name = thread.coach?.name || thread.coach?.email || "Your coach";
    return (
        <div
            data-testid="member-conversation-header"
            className="flex items-center gap-3 px-1 mb-3"
        >
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-red/20 text-brand-red text-sm font-display">
                {(name || "C")[0].toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
                <div className="text-base font-semibold text-white truncate">{name}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500">Maximum Sports Conditioning</div>
            </div>
        </div>
    );
}


function MessageBubble({ m, myUserId }) {
    const mine = m.sender_user_id === myUserId;
    const time = fmtTime(m.sent_at);
    const read = !!m.read_at_by_recipient;
    return (
        <li
            data-testid={`member-message-${m.message_id}`}
            data-mine={mine ? "1" : "0"}
            className={`flex ${mine ? "justify-end" : "justify-start"}`}
        >
            <div className={`max-w-[78%] ${mine ? "items-end" : "items-start"} flex flex-col`}>
                {!mine && (
                    <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1 ml-1">
                        {m.sender_name || "Coach"}
                    </div>
                )}
                <div
                    className={`px-3.5 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
                        mine
                            ? "bg-brand-red text-white rounded-br-sm"
                            : "bg-zinc-800 text-zinc-100 rounded-bl-sm"
                    }`}
                >
                    {m.body}
                </div>
                <div className={`text-[10px] text-zinc-500 mt-1 ${mine ? "mr-1 inline-flex items-center gap-1" : "ml-1"}`}>
                    <span>{time}</span>
                    {mine && read && (
                        <CheckCheck className="w-3 h-3 text-brand-red" title="Seen" />
                    )}
                </div>
            </div>
        </li>
    );
}


function EmptyState({ needsPicker, hasPicked }) {
    return (
        <div className="flex flex-col items-center justify-center h-full py-10 text-center">
            <MessageSquare className="w-10 h-10 text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-300 font-semibold mb-1">
                {needsPicker
                    ? (hasPicked ? "Type below to send your first message" : "Pick a coach above to start")
                    : "No messages yet"}
            </p>
            <p className="text-xs text-zinc-500 max-w-sm">
                Your conversation with your coach lives here. Ask about scheduling, programs, billing — anything.
            </p>
        </div>
    );
}
